import { useCallback, useEffect, useRef, useState } from "react";
import { Socket_Events } from "../utils/enums";
import { ChatMessage, NewMessageRequest, SocketMessageResponse } from "@/configs/@types/socket";
import useRequestsStore from "@/store/useRequestStore";
import ENDPOINTS from "@/configs/endpoints";
import { useChatStore } from "@/store/useChatsStore";
import { useAuthStore } from "@/store/useAuthStore";
import { useNotificationStore } from "@/store/useNotificationStore";
import { toast } from "sonner";
import dayjs from "dayjs";
import notificationSoundUrl from "@/assets/sounds/notification.mp3";
import { isOnActiveChatsPageRef } from "@/utils/activeChatsPageRef";


export function useSocket(roomId?: string, authToken?: string) {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [connected, setConnected] = useState(false);
    const userId = useAuthStore((state) => state.user?.userId);
    const user = useAuthStore((state) => state.user);
    const sessionLocked = useAuthStore((state) => state.sessionLocked);
    const setSessionLockMessage = useAuthStore((state) => state.setSessionLockMessage);
    const socketRef = useRef<WebSocket | null>(null);
    const notificationAudioRef = useRef<HTMLAudioElement | null>(null);
    const reconnectAttemptsRef = useRef(0);
    const reconnectTimerRef = useRef<number | null>(null);
    const roomIdRef = useRef(roomId);
    const isFirstConnectionRef = useRef(true);
    const blockReconnectRef = useRef(false);
    const disconnectTimeRef = useRef<number | null>(null);
    const lastNetworkErrorToastRef = useRef(0);
    const offlineTimeRef = useRef<number | null>(null);
    const offlineTimerRef = useRef<number | null>(null);
    // const location = useLocation();
    const pageVisibleRef = useRef(true);
    // Get store functions
    const requestsStore = useRequestsStore();
    const chatStore = useChatStore();
    const activeChatId = useChatStore((state) => state.activeChatId);
    const unreadByChatRef = useRef<Record<string, number>>({});


    console.log(requestsStore, ":::requestsStore")

    const addMessage = (msg: ChatMessage) => {
        setMessages((prev) => [...prev, msg]);
    };

    const getChatToastId = (chatId: string) => `chat-notification-${chatId}`;



    const showIncomingMessageNotification = (chatId: string, message: string, count: number, displayName?: string, _shouldPlaySound?: boolean) => {
        const text = (message || "New message").trim();
        const name = displayName;
        const title = `${name}: ${count} new message${count > 1 ? "s" : ""}`;

        toast.info(title, {
            id: getChatToastId(chatId),
            position: "top-right",
            description: text.length > 80 ? `${text.slice(0, 80)}...` : text,
            duration: 6000,
            closeButton: true,
        });

        if (typeof window !== "undefined" && "Notification" in window) {
            if (Notification.permission === "granted") {
                new Notification(title, {
                    body: text.length > 120 ? `${text.slice(0, 120)}...` : text,
                    tag: `chat-${chatId}`,
                });
            }
        }
    };


    const playNotificationSound = () => {
        try {
            let audio = notificationAudioRef.current;
            if (!audio) {
                audio = new Audio(notificationSoundUrl);
                audio.volume = 0.5;
            }
            audio.currentTime = 0;
            audio.play().catch((e) => {
                console.warn("Unable to play notification sound", e);
            });
        } catch (err) {
            console.warn("Error playing notification sound", err);
        }
    };

    useEffect(() => {
        if (typeof window === "undefined" || !("Notification" in window)) {
            return;
        }

        const audio = new Audio(notificationSoundUrl);
        audio.preload = "auto";
        audio.volume = 0.5;

        notificationAudioRef.current = audio;

        if (Notification.permission === "default") {
            Notification.requestPermission().catch(() => {
                // Ignore permission request failures
            });
        }
    }, []);
    // 🔓 Unlock audio after first user interaction
    useEffect(() => {
        const unlockAudio = () => {
            const audio = notificationAudioRef.current;
            if (!audio) return;

            audio.play()
                .then(() => {
                    audio.pause();
                    audio.currentTime = 0;
                    console.log("✅ Audio unlocked");
                })
                .catch(() => { });

            window.removeEventListener("click", unlockAudio);
            window.removeEventListener("keydown", unlockAudio);
        };

        window.addEventListener("click", unlockAudio);
        window.addEventListener("keydown", unlockAudio);

        return () => {
            window.removeEventListener("click", unlockAudio);
            window.removeEventListener("keydown", unlockAudio);
        };
    }, []);

    // Track page visibility and route
    useEffect(() => {
        const handleVisibilityChange = () => {
            pageVisibleRef.current = !document.hidden;
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    useEffect(() => {
        if (typeof window === "undefined" || !("Notification" in window)) {
            return;
        }

        if (Notification.permission === "default") {
            Notification.requestPermission().catch(() => {
                // Ignore permission request failures
            });
        }
    }, []);

    useEffect(() => {
        if (!activeChatId) {
            return;
        }

        unreadByChatRef.current[activeChatId] = 0;
        toast.dismiss(getChatToastId(activeChatId));
    }, [activeChatId]);

    useEffect(() => {
        const effectId = Math.random().toString(36).slice(2, 8);
        console.log(`[SOCKET-EFFECT-${effectId}] STARTED - authToken: ${!!authToken}, sessionLocked: ${sessionLocked}`);

        blockReconnectRef.current = sessionLocked;
        roomIdRef.current = roomId;
        if (!authToken) {
            console.log(`[SOCKET-EFFECT-${effectId}] No authToken, skipping WebSocket connection`);
            return;
        }

        if (sessionLocked) {
            console.warn(`[SOCKET-EFFECT-${effectId}] Session locked; skipping WebSocket connection`);
            return;
        }

        const scheduleReconnect = () => {
            if (blockReconnectRef.current) {
                console.warn("Reconnect blocked due to session lock");
                return;
            }
            if (reconnectAttemptsRef.current >= 3) {
                console.warn("Max WebSocket reconnect attempts reached");
                return;
            }
            const attempt = reconnectAttemptsRef.current;
            const delay = Math.min(1000 * Math.pow(2, attempt), 5000);
            reconnectAttemptsRef.current += 1;
            const reconnectTime = new Date().toLocaleTimeString();
            console.log(
                `[${reconnectTime}] Scheduling WebSocket reconnect attempt ${reconnectAttemptsRef.current} in ${delay}ms`
            );
            reconnectTimerRef.current = window.setTimeout(() => {
                const attemptTime = new Date().toLocaleTimeString();
                const timeSinceDisconnect = disconnectTimeRef.current
                    ? Math.round((Date.now() - disconnectTimeRef.current) / 1000)
                    : 'unknown';
                console.log(
                    `[${attemptTime}] Attempting reconnection. Time since disconnect: ${timeSinceDisconnect}s`
                );
                connect();
            }, delay);
        };

        const connect = () => {
            if (
                socketRef.current &&
                (socketRef.current.readyState === WebSocket.OPEN ||
                    socketRef.current.readyState === WebSocket.CONNECTING)
            ) {
                return;
            }

            // Clear sessionId only on first connection (new connection, not reconnection)
            if (isFirstConnectionRef.current) {
                useAuthStore.getState().setSocketSessionId("");
                isFirstConnectionRef.current = false;
            }

            console.log("Attempting WebSocket connection...", useAuthStore.getState().socketSessionId);

            const socket = new WebSocket(
                `${ENDPOINTS.WS.CONNECT}?token=${authToken}&sessionId=${useAuthStore.getState().socketSessionId || ""}`
            );
            console.log("Connecting to WebSocket:", `${ENDPOINTS.WS.CONNECT}?token=${authToken}&sessionId=${useAuthStore.getState().socketSessionId || ""}`);
            socketRef.current = socket;

            socket.onopen = () => {
                const connectTime = new Date().toLocaleTimeString();
                const timeSinceDisconnect = disconnectTimeRef.current
                    ? Math.round((Date.now() - disconnectTimeRef.current) / 1000)
                    : null;
                if (timeSinceDisconnect !== null) {
                    console.log(`[${connectTime}] ✓ WebSocket reconnected successfully after ${timeSinceDisconnect}s`);
                } else {
                    console.log(`[${connectTime}] ✓ WebSocket connected`);
                }
                disconnectTimeRef.current = null;
                console.log(`[LIFECYCLE] Setting connected = true`);
                setConnected(true);
                reconnectAttemptsRef.current = 0;
                if (reconnectTimerRef.current) {
                    clearTimeout(reconnectTimerRef.current);
                    reconnectTimerRef.current = null;
                }

                requestsStore.setAcceptHandler(async (request) => {
                    const cname = request?.cname || request?.payload?.cname;
                    const queueId = useChatStore.getState().chats[request.id]?.queueId;
                    const sessionId = useAuthStore.getState().socketSessionId;
                    const customerId = request?.customerId || request?.payload?.customerId;
                    console.log('Accept handler - request:', request);
                    console.log('Accept handler - extracted cname:', cname, 'customerId:', customerId);
                    const body = {
                        requestType: Socket_Events.ACCEPT_CHAT,
                        chatId: request.id,
                        sessionId: sessionId,
                        agentId: userId,
                        cname: cname,
                        customerId: customerId,
                        queueId: queueId,
                        agentName: `${user?.firstName} ${user?.lastName}`,
                    };
                    socket.send(JSON.stringify(body));

                    // Store cname and customerId in chat store
                    if (cname && request.id) {
                        console.log('Calling setCname with chatId:', request.id, 'cname:', cname);
                        chatStore.setCname(request.id, cname);
                    } else {
                        console.log('NOT calling setCname - cname:', cname, 'chatId:', request.id);
                    }
                    if (customerId && request.id) {
                        console.log('Calling setCustomerId with chatId:', request.id, 'customerId:', customerId);
                        chatStore.setCustomerId(request.id, customerId);
                    }
                    if (request.channel && request.id) {
                        console.log('Calling setChannel with chatId:', request.id, 'channel:', request.channel);
                        chatStore.setChannel(request.id, request.channel);
                    }
                });
            };

            socket.onclose = (e) => {
                const disconnectTime = new Date().toLocaleTimeString();
                disconnectTimeRef.current = Date.now();
                console.log(`[${disconnectTime}] ✗ WebSocket disconnected (code: ${e.code}, reason: ${e.reason})`);
                console.log(`[LIFECYCLE] Setting connected = false`);
                setConnected(false);
                if (e.code === 1003) {
                    const { logout, isLoggingOut } = useAuthStore.getState();
                    if (!isLoggingOut) {
                        logout()
                            .catch((logoutError) => console.error('Auto-logout failed', logoutError))
                            .finally(() => {
                                console.log('Logged out due to WebSocket 1003 closure');
                            });
                    }
                }
                if (e.code === 1011) {
                    console.error("Server internal error, not reconnecting");
                    return;
                }
                scheduleReconnect();
            };

            socket.onerror = (error: Event) => {
                console.error("WebSocket error:", error);

                // Check if it's a network/connection error
                const errorEvent = error as ErrorEvent;
                if (errorEvent.message?.includes('ERR_INTERNET_DISCONNECTED') ||
                    errorEvent.message?.includes('net::') ||
                    !navigator.onLine) {
                    const now = Date.now();
                    // Throttle toast to once every 5 seconds (same as REST API)
                    if (now - lastNetworkErrorToastRef.current > 5000) {
                        lastNetworkErrorToastRef.current = now;
                        toast.error('Internet connection has been disconnected. Please try again later.');
                    }
                }

                scheduleReconnect();
            };

            socket.onmessage = (event) => {
                const data: SocketMessageResponse = JSON.parse(event.data);

                console.log(data, "From Socket ");
                const requestType = data.requestType;

                console.log(requestType);

                // Prevent multiple sessions
                if (requestType === Socket_Events.SESSION_ALREADY_ACTIVE) {
                    blockReconnectRef.current = true;
                    useAuthStore.getState().setSessionLocked(true);
                    setSessionLockMessage("You have already an active session.");
                    // Stop any pending queue fetches and clear pending list
                    requestsStore.setQueueFetched(true);
                    requestsStore.clearPendingRequests();
                    setConnected(false);
                    if (socketRef.current) {
                        socketRef.current.close();
                    }
                    return;
                }

                // Handle user deleted event
                if (requestType === Socket_Events.USER_DELETED) {
                    console.log("[SOCKET] User deleted event received, logging out...");
                    const { logout } = useAuthStore.getState();
                    logout().catch((err) => console.error("Auto-logout failed", err));
                    return;
                }

                // Handle new chat request
                if (requestType === Socket_Events.NEW_CHAT) {
                    console.log("New chat request received via socket", data);
                    const chatId = data?.chatId || `${Date.now()}`;
                    requestsStore.addRequest({
                        id: String(chatId),
                        userId: data?.userId,
                        text: data?.text || `New chat request`,
                        timestamp: dayjs().format("DD-MMM-YYYY, h:mm A"),
                        payload: data,
                        cname: data?.cname,
                        customerId: data?.customerId,
                        queueId: data?.queueId,
                        channel: data?.channel,
                    });
                    // Store queueId in chat store immediately so it's available for all events
                    if (data?.queueId && chatId) {
                        chatStore.setQueueId(String(chatId), data.queueId);
                    }
                    if (data?.channel && chatId) {
                        chatStore.setChannel(String(chatId), data.channel);
                    }
                    // Play sound for new incoming request (when on another tab or another page)
                    const isTabVisible = pageVisibleRef.current;
                    const isOnActiveChatsPage = isOnActiveChatsPageRef.current;
                    if (!isTabVisible || !isOnActiveChatsPage) {
                        playNotificationSound();
                    }
                }

                if (requestType === Socket_Events.CONNECTED) {
                    if (data?.sessionId) {
                        useAuthStore.getState().setSocketSessionId(data.sessionId);
                        console.log(`[MESSAGE-HANDLER] CONNECTED received, sessionId set to: "${data.sessionId}"`);
                    }
                    if (data?.domainCode) {
                        useAuthStore.getState().setDomainCode(data.domainCode);
                    }
                }

                if (requestType === Socket_Events.ACCEPT_BY_OTHER) {
                    if (data?.chatId) {
                        requestsStore.removeRequest(data.chatId);
                    }
                }

                if (requestType === Socket_Events.CHAT_ALREADY_ACCEPTED) {
                    if (data?.chatId) {
                        requestsStore.removeRequest(data.chatId);
                        chatStore.setStatus(data.chatId, 'inactive');
                        requestsStore.removeAcceptedChat(data.chatId);
                        toast.info(`Chat is already accepted by another agent`);
                    }
                }

                if (requestType === Socket_Events.AUTO_REJECT) {
                    if (data?.chatId) {
                        requestsStore.removeRequest(data.chatId);
                    }
                }

                if (requestType === Socket_Events.CUSTOMER_LEFT_CHAT) {
                    if (data?.chatId) {
                        console.log('Customer left chat:', data.chatId);
                        chatStore.markCustomerLeftChat(data.chatId);
                    }
                }

                // Handle admin message (your message)
                if (requestType === Socket_Events.ADMIN_MESSAGE) {
                    const message: ChatMessage = {
                        id: `${Date.now()}-${Math.random()
                            .toString(16)
                            .slice(2)}`,
                        text: data.text || JSON.stringify(data),
                        sender: "server",
                        timestamp: data.ts || Date.now(),
                        userId: data.userId,
                        chatId: data.chatId,
                    };

                    // Add to local state (legacy)
                    addMessage(message);

                    // Add to chat store by chatId
                    if (data.chatId) {
                        chatStore.addMessage(data.chatId, message);
                    }
                }

                // Handle user message (incoming from user)
                if (requestType === Socket_Events.USER_MESSAGE || requestType === "USER_MESSAGE") {
                    const incomingChatId = String(data.chatId || "");
                    const message: ChatMessage = {
                        id: `${Date.now()}-${Math.random()
                            .toString(16)
                            .slice(2)}`,
                        text: data.text || "",
                        sender: "user",
                        timestamp: Date.now(),
                        userId: data.userId,
                        chatId: data.chatId,
                        isMe: false,
                    };

                    // Add to local state (legacy)
                    addMessage(message);

                    // Add to chat store by chatId
                    if (data.chatId) {
                        chatStore.addMessage(data.chatId, message);
                    }

                    // Show toast when: on another page (e.g. previous chats) OR message is for a different chat than the one open on active chats
                    const currentActiveChatId = useChatStore.getState().activeChatId;
                    const isOnActiveChatsPage = isOnActiveChatsPageRef.current;
                    const isDifferentChat = currentActiveChatId !== incomingChatId;
                    const shouldShowToast = incomingChatId && (!isOnActiveChatsPage || isDifferentChat);

                    if (shouldShowToast) {
                        const nextCount = (unreadByChatRef.current[incomingChatId] || 0) + 1;
                        unreadByChatRef.current[incomingChatId] = nextCount;
                        // Get cname from chat store or fallback to generic "New Message"
                        const chatData = chatStore.chats[incomingChatId];
                        const displayName = chatData?.cname || data.cname || "New Message";

                        showIncomingMessageNotification(incomingChatId, data.text || "New message", nextCount, displayName);
                    }

                    // Play sound only when on another tab or another page (not on active chats with tab focused)
                    const isTabVisible = pageVisibleRef.current;
                    const shouldPlaySound = !isTabVisible || !isOnActiveChatsPage;
                    if (shouldPlaySound) {
                        playNotificationSound();
                    }
                }

                // Handle ticket notifications
                if (requestType === Socket_Events.NEW_TICKET_NOTIFICATION) {
                    useNotificationStore.getState().addNotification({
                        title: "New Ticket Created",
                        description: `A new ticket ${data.ticketId || ""} has been created.`,
                        type: "ticket",
                        metadata: data
                    });
                    useNotificationStore.getState().incrementTicketEventCount();
                    playNotificationSound();
                }

                if (requestType === Socket_Events.TICKET_ASSIGNED_TO_AGENT) {
                    useNotificationStore.getState().addNotification({
                        title: "Ticket Assigned",
                        description: `Ticket ${data.ticketId || ""} has been assigned to you.`,
                        type: "ticket",
                        metadata: data
                    });
                    useNotificationStore.getState().incrementTicketEventCount();
                    playNotificationSound();
                }

                if (requestType === Socket_Events.ESCALATE_TO_SUPERVISOR) {
                    useNotificationStore.getState().addNotification({
                        title: "Ticket Escalated",
                        description: `Ticket ${data.ticketId || ""} has been escalated to supervisor.`,
                        type: "ticket",
                        metadata: data
                    });
                    useNotificationStore.getState().incrementTicketEventCount();
                    playNotificationSound();
                }

                if (requestType === Socket_Events.TICKET_RESOLUTION_NOTIFICATION) {
                    useNotificationStore.getState().addNotification({
                        title: "Ticket Resolved",
                        description: `Ticket ${data.ticketId || ""} has been resolved.`,
                        type: "ticket",
                        metadata: data
                    });
                    useNotificationStore.getState().incrementTicketEventCount();
                    playNotificationSound();
                }
            };
        };

        connect();

        // Handle internet connectivity changes
        const handleOnline = () => {
            console.log('✓ Internet connection restored');
            toast.success('Internet connection restored');
            lastNetworkErrorToastRef.current = 0; // Reset throttle timer
            offlineTimeRef.current = null; // Clear offline timestamp

            // Clear 20-second timeout if it was set
            if (offlineTimerRef.current) {
                clearTimeout(offlineTimerRef.current);
                offlineTimerRef.current = null;
                console.log('Offline timer cleared - connection restored within 20 seconds');
            }

            // Clear old session ID on reconnection (force new session)
            useAuthStore.getState().setSocketSessionId("");
            console.log('Session ID cleared for fresh connection');
        };

        const handleOffline = () => {
            console.log('✗ Internet connection lost');
            const now = Date.now();
            offlineTimeRef.current = now;

            if (now - lastNetworkErrorToastRef.current > 5000) {
                lastNetworkErrorToastRef.current = now;
                toast.error('Internet connection has been disconnected. Please try again later.');
            }

            // Set 20-second timer to end all active chats if connection not restored
            if (!offlineTimerRef.current) {
                offlineTimerRef.current = window.setTimeout(() => {
                    console.log('⚠️ Internet offline for 20+ seconds - ending all active chats');
                    toast.error('Connection lost for too long. Ending all active chats.');

                    // End all active chats - use getState() to get current store state
                    const currentChatStore = useChatStore.getState();
                    const currentChats = currentChatStore.chats;
                    console.log('Current chats at timeout:', Object.keys(currentChats).length, 'chats found');

                    Object.keys(currentChats).forEach(chatId => {
                        console.log('Ending chat:', chatId);
                        currentChatStore.setStatus(chatId, 'inactive');
                    });

                    // Clear session ID
                    useAuthStore.getState().setSocketSessionId("");
                    const currentRequestsStore = useRequestsStore.getState();
                    currentRequestsStore.clearPendingRequests();
                    currentRequestsStore.setQueueFetched(false);

                    offlineTimerRef.current = null;
                }, 30000); // 30 seconds

                console.log('Offline timer started - will end chats if not reconnected in 30s');
            }
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            console.log(`[SOCKET-EFFECT-${effectId}] CLEANUP: Removing event listeners, clearing timers, closing socket`);
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }
            if (offlineTimerRef.current) {
                clearTimeout(offlineTimerRef.current);
                offlineTimerRef.current = null;
            }
            if (socketRef.current) {
                socketRef.current.onclose = null;
                socketRef.current.onerror = null;
                socketRef.current.onopen = null;
                socketRef.current.onmessage = null;
                socketRef.current.close();
                console.log(`[SOCKET-EFFECT-${effectId}] WebSocket disconnected on cleanup`);
                // Don't clear pending requests on cleanup - only on actual socket disconnect
                setConnected(false);
            }
        };
    }, [authToken, sessionLocked]);

    const sendMessage = useCallback(
        (text: string, chatIdParam?: string) => {
            const chatId = chatIdParam || roomId;

            console.log(chatId, "====chatId")
            console.log(connected, "===connected")
            console.log(socketRef.current, "======socketRef.current")

            const localMessage: ChatMessage = {
                id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
                text,
                sender: "user",
                isMe: true,
                timestamp: Date.now(),
                userId: undefined,
                chatId,
            };


            console.log(connected && socketRef.current && chatId, "::able to send")
            if (connected && socketRef.current && chatId) {
                const sessionId = useAuthStore.getState().socketSessionId;
                const domainCode = useAuthStore.getState().domainCode;
                const chatData = chatStore.chats[chatId];

                console.log(sessionId, "::sessionId")

                const payload: NewMessageRequest = {
                    requestType: Socket_Events.AGENT_MESSAGE,
                    userId: "",
                    adminId: "",
                    text,
                    chatId,
                    domainId: "",
                    domainCode: domainCode || "",
                    sessionId: sessionId || undefined,
                    agentId: userId || "",
                    agentName: `${user?.firstName} ${user?.lastName}`,
                    customerId: chatData?.customerId || "",
                    cname: chatData?.cname || "",
                    queueId: chatData?.queueId || "",
                };


                console.log(payload, "::payload")
                socketRef.current.send(JSON.stringify(payload));

                console.log("::::sent")
            }

            // Optimistic UI
            addMessage(localMessage);
            if (chatId) {
                chatStore.addMessage(chatId, localMessage);
            }
        },
        [roomId, connected]
    );


    const sendTyping = useCallback(
        (chatIdParam?: string) => {
            const chatId = chatIdParam || roomId;


            console.log(chatId, "====chatId for typing")
            console.log(connected, "===connected for typing")
            console.log(socketRef.current, "======socketRef.current for typing")
            if (connected && socketRef.current && chatId) {
                const sessionId = useAuthStore.getState().socketSessionId;
                const domainCode = useAuthStore.getState().domainCode;

                // Send AGENT_TYPING event
                const typingPayload = {
                    requestType: Socket_Events.AGENT_TYPING,
                    chatId,
                    sessionId: sessionId || undefined,
                    agentId: userId || "",
                    agentName: `${user?.firstName} ${user?.lastName}`,
                    domainCode: domainCode || "",
                    queueId: chatStore.chats[chatId]?.queueId || "",
                };
                console.log(typingPayload, "::sendTyping - sending AGENT_TYPING for chat:", chatId);
                socketRef.current.send(JSON.stringify(typingPayload));
            }
        },
        [roomId, connected]
    );

    return {
        messages,
        connected,
        sendMessage,
        sendTyping,
        socket: socketRef.current,
    } as const;
}